import { questionsApiClient, usersApiClient } from './api';
import authService from './authService';

export interface Question {
  _id: string;
  type: 'multiple-choice' | 'true-false' | 'fill-blank' | 'scenario';
  difficulty: 'beginner' | 'intermediate' | 'hard';
  text: string;
  options: {
    text: string;
    isCorrect: boolean;
  }[];
  explanation: string;
  bibleReference: {
    book: string;
    chapter: number;
    verses: string;
    text?: string;
  };
  categories: string[];
  points: number;
  imageUrl?: string;
  scenarioDetails?: {
    story: string;
    outcomes: {
      choiceText: string;
      resultText: string;
      isRecommended: boolean;
      points: number;
    }[];
  };
  stats: {
    timesAnswered: number;
    timesCorrect: number;
    averageResponseTime: number;
  };
  isActive: boolean;
  createdBy: string;
  reviewedBy?: string;
}

export interface QuestionParams {
  type?: string;
  difficulty?: string;
  category?: string;
  book?: string;
  limit?: number;
  page?: number;
  isActive?: boolean;
}

export interface QuestionsResponse {
  status: string;
  data: {
    questions: Question[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
}

export interface QuestionResponse {
  status: string;
  data: {
    question: Question;
  };
}

export interface RandomQuestionsParams {
  type?: string;
  difficulty?: string;
  difficulties?: string[];
  categories?: string[];
  count?: number;
  excludeIds?: string[];
}

export interface ProgressionQuestionsResponse {
  questions: Question[];
  userTier: string;
  userRank: string;
  pointsPerQuestion: number;
}

// Utility function to shuffle array using Fisher-Yates algorithm
const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array]; // Create a copy to avoid mutating original
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const questionService = {
  getQuestions: async (params: QuestionParams = {}): Promise<QuestionsResponse> => {
    try {
      const response = await questionsApiClient.get<QuestionsResponse>('/questions', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getQuestion: async (id: string): Promise<Question> => {
    try {
      const response = await questionsApiClient.get<QuestionResponse>(`/questions/${id}`);
      return response.data.data.question;
    } catch (error) {
      throw error;
    }
  },
  
  // Get questions with rank-based progression for authenticated users
  getQuestionsForUser: async (limit: number = 15): Promise<Question[]> => {
    try {
      const auth0User = authService.getAuth0User();
      if (!auth0User?.sub) {
        throw new Error('User not authenticated');
      }

      const auth0Id = encodeURIComponent(auth0User.sub);
      const response = await usersApiClient.get<ProgressionQuestionsResponse>(`/questions/for-user/${auth0Id}?limit=${limit}`);
      
      console.log('✅ Got progression questions:', {
        count: response.data.questions.length,
        userRank: response.data.userRank,
        userTier: response.data.userTier,
        pointsPerQuestion: response.data.pointsPerQuestion
      });
      
      return response.data.questions;
    } catch (error) {
      console.error('Failed to get progression questions:', error);
      throw error;
    }
  },

  // Get random questions from website API with answer shuffling
  getRandomQuestions: async (params: RandomQuestionsParams): Promise<Question[]> => {
    try {
      const response = await questionsApiClient.post<{status: string; data: {questions: Question[]}}>('/questions/random', params);
      const questions = response.data.data.questions;
      
      // Shuffle answer options for each question to prevent memorization
      const shuffledQuestions = questions.map(question => ({
        ...question,
        options: shuffleArray(question.options)
      }));
      
      console.log('✅ Got random questions with shuffled answers:', shuffledQuestions.length);
      return shuffledQuestions;
    } catch (error) {
      throw error;
    }
  },
  
  createQuestion: async (questionData: Partial<Question>): Promise<Question> => {
    try {
      const response = await questionsApiClient.post<QuestionResponse>('/questions', questionData);
      return response.data.data.question;
    } catch (error) {
      throw error;
    }
  },
  
  updateQuestion: async (id: string, questionData: Partial<Question>): Promise<Question> => {
    try {
      const response = await questionsApiClient.put<QuestionResponse>(`/questions/${id}`, questionData);
      return response.data.data.question;
    } catch (error) {
      throw error;
    }
  },
  
  deleteQuestion: async (id: string): Promise<void> => {
    try {
      await questionsApiClient.delete(`/questions/${id}`);
    } catch (error) {
      throw error;
    }
  },
  
  updateQuestionStats: async (id: string, isCorrect: boolean, responseTime: number): Promise<void> => {
    try {
      await questionsApiClient.patch(`/questions/${id}/stats`, { isCorrect, responseTime });
    } catch (error) {
      throw error;
    }
  }
};

export default questionService; 